import { useEffect, useState } from "react";
import { Save, Sliders, Percent, CalendarClock, Building2, History, Plus, Trash2, KeyRound } from "lucide-react";
import { api } from "../lib/apiClient";
import { useApp } from "../lib/store";
import { PageHeader } from "../components/Shared";
import { Panel } from "../components/Filters";
import { toast } from "sonner";

export default function Settings() {
  const { can } = useApp();
  const [s, setS] = useState(null);
  const [audit, setAudit] = useState([]);

  useEffect(() => { api.settings().then(setS).catch(() => {}); api.auditLog().then(setAudit).catch(() => {}); }, []);

  if (!s) return <div className="py-20 text-center text-muted-c">Loading…</div>;
  const ro = !can.settings;

  const setGrade = (i, field, v) => { const g = [...s.grade_matrix]; g[i] = { ...g[i], [field]: Number(v) }; setS({ ...s, grade_matrix: g }); };
  const setDelay = (k, v) => setS({ ...s, content_delay_scores: { ...s.content_delay_scores, [k]: Number(v) } });
  const setDev = (k, v) => setS({ ...s, dev_scores: { ...s.dev_scores, [k]: Number(v) } });

  const save = async () => { await api.updateSettings(s); toast.success("Settings saved · rules applied system-wide"); };

  return (
    <div>
      <PageHeader title="Settings & Audit" subtitle="Configure business logic · grade bands, increment matrix, deadline scoring">
        {can.settings && <button onClick={save} className="pill px-4 py-2 text-sm font-semibold brand-bg text-white" data-testid="save-settings-button"><Save size={15} /> Save Changes</button>}
      </PageHeader>

      {ro && <div className="card-panel p-4 mb-5 text-sm text-secondary">Settings are read-only. Switch to <b className="brand-text">Super Admin</b> to edit business rules.</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Grade & Increment Matrix" testid="grade-matrix-panel">
          <table className="data-table">
            <thead><tr><th>Grade</th><th>Min Score</th><th>Increment %</th></tr></thead>
            <tbody>
              {s.grade_matrix.map((g, i) => (
                <tr key={g.grade}>
                  <td className="font-bold mono">{g.grade}</td>
                  <td><input disabled={ro} type="number" className="input-c py-1 mono" style={{ width: 90 }} value={g.min} onChange={(e) => setGrade(i, "min", e.target.value)} data-testid={`grade-min-${g.grade}`} /></td>
                  <td><input disabled={ro} type="number" className="input-c py-1 mono" style={{ width: 90 }} value={g.increment} onChange={(e) => setGrade(i, "increment", e.target.value)} data-testid={`grade-inc-${g.grade}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        <div className="space-y-6">
          <Panel title="Content Calendar Delay Scoring" testid="delay-scoring-panel">
            <div className="space-y-2">
              {[["0", "On/Before deadline"], ["1", "1 day late"], ["2", "2 days late"], ["3", "3 days late"], ["4", "4 days late"], ["5", "5+ days late"]].map(([k, l]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-sm text-secondary">{l}</span>
                  <input disabled={ro} type="number" className="input-c py-1 mono" style={{ width: 80 }} value={s.content_delay_scores[k]} onChange={(e) => setDelay(k, e.target.value)} data-testid={`delay-score-${k}`} />
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Development Buffer Rules" testid="dev-rules-panel">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-secondary">Buffer duration (hours)</span>
              <input disabled={ro} type="number" className="input-c py-1 mono" style={{ width: 80 }} value={s.dev_buffer_hours} onChange={(e) => setS({ ...s, dev_buffer_hours: Number(e.target.value) })} data-testid="buffer-hours-input" />
            </div>
            <div className="space-y-2">
              {[["on_time", "On/Before deadline"], ["within_buffer", "Within 48h buffer"], ["day1", "1 day after buffer"], ["day2", "2 days after"], ["day3", "3 days after"], ["day4plus", "4+ days after"]].map(([k, l]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-sm text-secondary">{l}</span>
                  <input disabled={ro} type="number" className="input-c py-1 mono" style={{ width: 80 }} value={s.dev_scores[k]} onChange={(e) => setDev(k, e.target.value)} data-testid={`dev-score-${k}`} />
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <BrandEditor s={s} setS={setS} ro={ro} onSave={save} />
        {can.settings && <UserAccounts />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Panel title="Departments & Designations" testid="org-panel">
          <p className="text-xs uppercase tracking-wider text-muted-c mb-2">Departments</p>
          <div className="flex flex-wrap gap-2 mb-4">{(s.departments || []).map((d) => <span key={d} className="pill px-3 py-1 text-xs border-subtle text-secondary">{d}</span>)}</div>
          <p className="text-xs uppercase tracking-wider text-muted-c mb-2">Designations</p>
          <div className="flex flex-wrap gap-2">{(s.designations || []).map((d) => <span key={d} className="pill px-3 py-1 text-xs border-subtle text-secondary">{d}</span>)}</div>
        </Panel>

        <Panel title="Audit History" testid="audit-panel">
          {audit.length ? audit.map((a) => (
            <div key={a.id} className="flex items-start gap-3 py-2 border-b border-subtle last:border-0">
              <History size={15} className="text-muted-c mt-0.5" />
              <div className="text-sm"><p className="font-medium">{a.action} · {a.value}%</p><p className="text-xs text-muted-c">{a.reason} — {(a.at || "").slice(0, 10)}</p></div>
            </div>
          )) : <p className="text-sm text-muted-c">No override history yet.</p>}
        </Panel>
      </div>
    </div>
  );
}

function BrandEditor({ s, setS, ro, onSave }) {
  const brands = s.brands || [];
  const setBrand = (i, field, v) => { const b = [...brands]; b[i] = { ...b[i], [field]: v }; setS({ ...s, brands: b }); };
  const add = () => setS({ ...s, brands: [...brands, { name: "New Brand", manager_id: "anjali", group: "" }] });
  const del = (i) => setS({ ...s, brands: brands.filter((_, idx) => idx !== i) });
  return (
    <Panel title="Brands & Manager Assignment" testid="brand-editor-panel"
      action={!ro && <button onClick={add} className="pill px-2.5 py-1 text-xs brand-bg text-white font-semibold" data-testid="add-brand-button"><Plus size={13} /> Add</button>}>
      <div className="space-y-2">
        {brands.map((b, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`brand-row-${i}`}>
            <input disabled={ro} value={b.name} onChange={(e) => setBrand(i, "name", e.target.value)} className="input-c py-1 flex-1" data-testid={`brand-name-${i}`} />
            <select disabled={ro} value={b.manager_id} onChange={(e) => setBrand(i, "manager_id", e.target.value)} className="input-c py-1" style={{ maxWidth: 130 }} data-testid={`brand-manager-${i}`}>
              <option value="anjali">Anjali</option>
              <option value="hani">Hani</option>
            </select>
            {!ro && <button onClick={() => del(i)} className="text-muted-c hover:text-red-500"><Trash2 size={15} /></button>}
          </div>
        ))}
        {!brands.length && <p className="text-sm text-muted-c">No brands yet.</p>}
      </div>
      {!ro && <div className="flex justify-end mt-4"><button onClick={onSave} className="pill px-3 py-1.5 text-xs font-semibold surface border-strong hover:brand-text transition-colors" data-testid="save-brands-button"><Save size={13} /> Save Brands</button></div>}
    </Panel>
  );
}

function UserAccounts() {
  const [users, setUsers] = useState([]);
  const [reset, setReset] = useState(null);
  const [pw, setPw] = useState("");
  useEffect(() => { api.users().then(setUsers).catch(() => {}); }, []);
  const doReset = async () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    try { await api.resetPassword(reset.id, pw); toast.success(`Password reset for ${reset.name}`); setReset(null); setPw(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Panel title="User Accounts & Password Reset" testid="user-accounts-panel">
      <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-3 py-1.5 border-b border-subtle last:border-0" data-testid={`user-row-${u.id}`}>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{u.name} <span className="text-xs text-muted-c">· {u.role}</span></p><p className="text-xs text-muted-c truncate">{u.email}</p></div>
            <button onClick={() => { setReset(u); setPw(""); }} className="pill px-2.5 py-1 text-xs border-subtle text-secondary hover:brand-text" data-testid={`reset-pw-${u.id}`}><KeyRound size={13} /> Reset</button>
          </div>
        ))}
      </div>
      {reset && (
        <div className="mt-4 p-3 elevated rounded-xl border border-subtle" data-testid="reset-pw-form">
          <p className="text-sm mb-2">New password for <b>{reset.name}</b></p>
          <div className="flex gap-2">
            <input type="password" className="input-c py-1 flex-1" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="min 6 characters" data-testid="reset-pw-input" />
            <button onClick={doReset} className="pill px-3 py-1.5 text-xs brand-bg text-white font-semibold" data-testid="confirm-reset-pw">Set</button>
            <button onClick={() => setReset(null)} className="pill px-3 py-1.5 text-xs border-strong">Cancel</button>
          </div>
        </div>
      )}
    </Panel>
  );
}
